import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Firebase Imports
import { logEvent } from 'firebase/analytics';
import { analytics, auth } from '../../firebase/firebase';
import {
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    sendPasswordResetEmail
} from 'firebase/auth';

// Component Imports
import AuthModal from '../auth/AuthModal';

// Icon Imports
import { Menu, X, Zap, Download } from 'lucide-react';

const googleProvider = new GoogleAuthProvider();

// Reusable Header Component (Matching Landing Page)
const Header = ({ openAuthModal }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();

    const navLinks = [
        { href: '/#features', label: 'Features' },
        { href: '/#how-to-use', label: 'How It Works' },
        { href: '/#pricing', label: 'Pricing' },
        { href: '/how-to-use', label: 'User Guide' },
        { href: '/blog', label: 'Blog' },
        { href: '/#faq', label: 'FAQ' },
    ];

    const isActive = (href) => {
        if (href.startsWith('/#')) return false;
        return location.pathname === href;
    };

    return (
        <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex-shrink-0">
                        <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel's Seller Tools</Link>
                    </div>
                    <nav className="hidden md:flex items-center space-x-2">
                        {navLinks.map((link) => (
                             <Link
                                key={link.label}
                                to={link.href}
                                className={`font-medium transition-colors px-3 py-2 rounded-md ${
                                    isActive(link.href)
                                        ? 'text-sky-600'
                                        : 'text-slate-600 hover:text-sky-600'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <button
                            onClick={() => {
                                logEvent(analytics, 'click_signup_trial', { source: 'Header_SignUp' });
                                openAuthModal('signup');
                            }}
                            className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-lg transition-transform transform hover:scale-105"
                        >
                            Sign Up Free
                        </button>
                        <a
                            href="/app"
                            onClick={() => logEvent(analytics, 'login', { method: 'Direct', source: 'Header_Login' })}
                            className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm"
                        >
                            Login
                        </a>
                    </nav>
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-slate-700"
                            aria-expanded={isMenuOpen}
                        >
                            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>
            {isMenuOpen && (
                <div className="md:hidden">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                onClick={() => setIsMenuOpen(false)}
                                className="text-slate-600 hover:bg-sky-50 hover:text-sky-700 block px-3 py-2 rounded-md text-base font-medium"
                            >
                                {link.label}
                            </Link>
                        ))}
                        <button onClick={() => { openAuthModal('signup'); setIsMenuOpen(false); }} className="w-full bg-sky-100 text-sky-700 block px-3 py-3 rounded-md text-base font-bold text-center mt-4">
                            Sign Up for Free
                        </button>
                        <a href="/app" className="w-full text-center block py-2 text-slate-600 font-medium">
                            Login
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

// Reusable Footer Component
const Footer = () => (
    <footer className="bg-slate-800 text-slate-400 py-12">
        <div className="max-w-5xl mx-auto text-center px-4">
            <p>&copy; 2025 Mr. Joel's Seller Tools. All Rights Reserved.</p>
            <div className="mt-4 space-x-4">
                <Link to="/contact" className="hover:text-white">Contact</Link>
                <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            </div>
        </div>
    </footer>
);


const HowToUsePage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('tab-extension');

    // --- AUTH MODAL STATE & LOGIC ---
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authMode, setAuthMode] = useState('signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');
    const [authError, setAuthError] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetMessage, setResetMessage] = useState('');

    const openAuthModal = (mode = 'signup') => {
        setAuthError('');
        setResetMessage('');
        setShowForgotPassword(false);
        setAuthMode(mode);
        setIsAuthModalOpen(true);
    };

    const handleAuthAction = async (mode) => {
        setAuthError('');
        try {
            if (mode === 'signup') {
                await createUserWithEmailAndPassword(auth, email, password);
                logEvent(analytics, 'sign_up', { method: 'Email' });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
                logEvent(analytics, 'login', { method: 'Email' });
            }
            setIsAuthModalOpen(false);
            navigate('/app');
        } catch (error) {
            setAuthError(error.message.replace('Firebase: ', ''));
        }
    };

    const handleGoogleSignIn = async () => {
        setAuthError('');
        try {
            await signInWithPopup(auth, googleProvider);
            logEvent(analytics, 'sign_up', { method: 'Google' });
            setIsAuthModalOpen(false);
            navigate('/app');
        } catch (error) {
            setAuthError(error.message.replace('Firebase: ', ''));
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setAuthError('');
        setResetMessage('');
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetMessage("Password reset email sent! Please check your inbox.");
        } catch (error) {
            setAuthError(error.message.replace('Firebase: ', ''));
        }
    };
    // --- END AUTH MODAL LOGIC ---


    const tabs = [
        { id: 'tab-extension', label: 'Chrome Extension' },
        { id: 'tab-import', label: 'Import Products' },
        { id: 'tab-rewrite', label: 'Rewrite Descriptions' },
        { id: 'tab-create', label: 'Create New Listings' },
        { id: 'tab-seo', label: 'Use SEO Tools' },
        { id: 'tab-social', label: 'Use Social Media' },
    ];

    const CHROME_EXTENSION_URL = "https://chromewebstore.google.com/detail/mr-joels-seller-tools-seo/neplkgjelpnenikeeampeiakadihknbc?utm_source=item-share-cb";

    return (
        <>
            <Helmet>
                <title>How to Use | Mr. Joel's TPT Tools</title>
                <meta name="description" content="Step-by-step guides and tutorials on how to get the most out of the TPT SEO and Description Generator app." />
            </Helmet>

            <div className="bg-slate-50 text-slate-900">
                <Header openAuthModal={openAuthModal} />

                <main className="py-16 sm:py-24">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Page Header */}
                        <div className="text-center">
                            <h1 className="text-4xl sm:text-5xl font-extrabold text-sky-800 tracking-tight">How to Use the App</h1>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">Step-by-step guides to master every feature and get the most out of your TPT workflow.</p>
                        </div>

                        {/* Tab Navigation */}
                        <div className="mt-12">
                            <div className="border-b border-slate-200">
                                <nav className="-mb-px flex justify-center space-x-4 sm:space-x-8 overflow-x-auto" aria-label="Tabs">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                                                activeTab === tab.id
                                                    ? 'text-sky-600 border-sky-600'
                                                    : 'text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                            }`}
                                        >
                                            {tab.id === 'tab-extension' && <Zap size={16} className="mr-2 text-amber-500 fill-amber-500" />}
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="mt-8">

                            {/* --- CHROME EXTENSION TAB (MATCHING STYLE) --- */}
                            {activeTab === 'tab-extension' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">New! The Chrome Extension Companion</h2>
                                        <p className="mt-4 text-slate-600">
                                            The extension works hand-in-hand with the web app, allowing you to audit and optimize directly on TPT pages.
                                            <a href={CHROME_EXTENSION_URL} target="_blank" rel="noopener noreferrer" className="ml-2 text-sky-600 underline font-semibold">Download it here.</a>
                                        </p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li><strong>Install & Pin:</strong> Get it from the Chrome Web Store, then click the puzzle icon (🧩) to pin it to your toolbar.</li>
                                            <li><strong>Open on TPT:</strong> Visit any of your product pages on TPT and click the <span className="font-bold text-amber-500">lightning bolt</span> on the right side.</li>
                                            <li><strong>Log In:</strong> Use your normal Seller Tools login details. Syncing may take a moment.</li>
                                            <li><strong>Use "The Coach":</strong> Instantly audit your listing for title keywords, description length, and tags (aim for 80+ score).</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video flex items-center justify-center border-2 border-slate-300 border-dashed">
                                         <div className="text-center p-8">
                                            <p className="text-slate-600 font-bold mb-2 text-lg">Download the Beta</p>
                                            <p className="text-slate-500 text-sm mb-6 max-w-xs mx-auto">Get the lightweight companion tool to run quick SEO checks directly on your TPT product pages.</p>
                                            <a
                                                href={CHROME_EXTENSION_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-6 rounded-full shadow-md transition-colors"
                                            >
                                                <Download size={18} className="mr-2" /> Install Extension
                                            </a>
                                         </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'tab-import' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">1. Import Your Products</h2>
                                        <p className="mt-4 text-slate-600">Start by getting your TPT products into the app. You can import a single product or your entire store at once.</p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li><strong>For a single product:</strong> Copy the product's URL from TPT and paste it into the "Add TPT Product by URL" form.</li>
                                            <li><strong>For your whole store:</strong> Click "Import All Products" in the sidebar and paste your main TPT store URL.</li>
                                            <li>The app will automatically fetch the titles and descriptions, adding them to your "My TPT Products" list.</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video">
                                        <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/q2GyjPQVb28" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'tab-rewrite' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">2. Rewrite & Enhance Descriptions</h2>
                                        <p className="mt-4 text-slate-600">Revitalize your old or underperforming listings with powerful rewriting tools.</p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li>Select a product from your list in the sidebar to load it.</li>
                                            <li>In the "Improve Existing Description" section, choose what you want to enhance (e.g., introduction, add themes).</li>
                                            <li>Select other products to automatically add backlinks for cross-selling.</li>
                                            <li>Click "Generate" to get a new, improved version of your description.</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video">
                                        <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/q2GyjPQVb28" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'tab-create' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">3. Create New Listings from Scratch</h2>
                                        <p className="mt-4 text-slate-600">Go from a simple idea to a fully formatted, SEO-friendly listing in minutes.</p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li>Start in the "Title Generator." Enter your product idea to get SEO-friendly title options based on competitor data.</li>
                                            <li>Select the title you like best.</li>
                                            <li>Move to the "Generate New Description" form and fill in the details about your resource.</li>
                                            <li>Click "Generate Description" to get a complete HTML description, ready for TPT.</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video">
                                        <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/q2GyjPQVb28" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'tab-seo' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">4. Use the SEO Rank Tracker</h2>
                                        <p className="mt-4 text-slate-600">Get the data you need to climb the TPT search rankings and out-maneuver competitors.</p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li>In the "SEO & Rank Tracker" tab, select a product to analyze.</li>
                                            <li>Add keywords you want to track, or generate new ideas.</li>
                                            <li>View your live ranking instantly in the table. Click "Update" for the latest data.</li>
                                            <li>Click "Full Analysis" to see a deep dive, including competitor keywords, winning phrases, and actionable suggestions.</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video">
                                        <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/ZeYOWrIC8AI" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'tab-social' && (
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800">5. Generate Social Media Content</h2>
                                        <p className="mt-4 text-slate-600">Create a month's worth of marketing content in minutes to drive traffic to your store.</p>
                                        <ol className="mt-6 list-decimal list-inside space-y-3 text-slate-600">
                                            <li>Go to the "Social Media Marketing" tab and select a product.</li>
                                            <li>Define your goal (e.g., a sale) and choose your tone.</li>
                                            <li>Click "Generate Posts." The app creates 3 unique posts for Facebook, Instagram, Pinterest, and X.</li>
                                            <li>Copy any post with a single click, or save the entire set for later.</li>
                                        </ol>
                                    </div>
                                    <div className="bg-slate-200 rounded-lg aspect-video">
                                        <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/jM1UCwIp6zY" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                <Footer />
            </div>

            {/* Auth Modal Logic */}
            {isAuthModalOpen && (
                <AuthModal
                    onClose={() => setIsAuthModalOpen(false)}
                    authMode={authMode}
                    setAuthMode={setAuthMode}
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    authError={authError}
                    setAuthError={setAuthError}
                    showForgotPassword={showForgotPassword}
                    setShowForgotPassword={setShowForgotPassword}
                    resetEmail={resetEmail}
                    setResetEmail={setResetEmail}
                    resetMessage={resetMessage}
                    setResetMessage={setResetMessage}
                    tptStoreUrlInput={tptStoreUrlInput}
                    setTptStoreUrlInput={setTptStoreUrlInput}
                    handleAuthAction={handleAuthAction}
                    handlePasswordReset={handlePasswordReset}
                    handleGoogleSignIn={handleGoogleSignIn}
                />
            )}
        </>
    );
};

export default HowToUsePage;